import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ShiftModel } from '../model/shift.model';
import { ShiftService } from '../shift.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-shift-edit',
  templateUrl: './shift-edit.component.html',
  styleUrls: ['./shift-edit.component.scss'],
  standalone: false
})
export class ShiftEditComponent implements DirtyCheck {
  title: string = "New Shift";
  editForm: FormGroup;
  shiftInfo: ShiftModel = {
    code: '',
    name: '',
    start_time: '09:00',
    end_time: '17:00',
    is_active: 1,
    is_deleted: 0
  };
  isFormDirty: boolean = false;
  permission: any;

  hours: string[] = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  minutes: string[] = ['00', '15', '30', '45'];

  start_hour: string = '';
  start_min: string = '';
  end_hour: string = '';
  end_min: string = '';

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private shiftService: ShiftService,
    private snackBarService: SnackBarService,
    private permissionService: PermissionService
  ) {
    let id = this.route.snapshot.paramMap.get('id');
    this.editForm = this.builder.group({
      code: [this.shiftInfo.code, [Validators.required, Validators.maxLength(50)]],
      name: [this.shiftInfo.name, [Validators.required, Validators.maxLength(100)]],
      start_time: [this.shiftInfo.start_time, Validators.required],
      end_time: [this.shiftInfo.end_time, Validators.required],
      description: [this.shiftInfo.description],
      is_active: [this.shiftInfo.is_active]
    });

    this.editForm.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });

    if (id !== null && parseInt(id) !== 0) {
      this.setShiftInfo(id);
    }
    this.permission = this.permissionService.getRowPermission(this.permissionService.getPagePermission("shift"), id);

    // Initialize dropdown values
    const startParts = this.splitTime(this.editForm.get('start_time')?.value || '09:00');
    this.start_hour = startParts.hour;
    this.start_min = startParts.min;

    const endParts = this.splitTime(this.editForm.get('end_time')?.value || '17:00');
    this.end_hour = endParts.hour;
    this.end_min = endParts.min;

    // Value changes subscriptions to auto-update local variables
    this.editForm.get('start_time')?.valueChanges.subscribe(val => {
      const parts = this.splitTime(val);
      this.start_hour = parts.hour;
      this.start_min = parts.min;
    });

    this.editForm.get('end_time')?.valueChanges.subscribe(val => {
      const parts = this.splitTime(val);
      this.end_hour = parts.hour;
      this.end_min = parts.min;
    });
  }

  private splitTime(time: string) {
    if (!time || time === '00:00' || time === '00:00:00') {
      return { hour: '', min: '' };
    }
    const parts = time.split(':');
    if (parts.length >= 2) {
      const h = parts[0].padStart(2, '0');
      const mVal = parseInt(parts[1], 10);
      let m = '00';
      if (mVal >= 8 && mVal < 23) m = '15';
      else if (mVal >= 23 && mVal < 38) m = '30';
      else if (mVal >= 38) m = '45';
      return { hour: h, min: m };
    }
    return { hour: '', min: '' };
  }

  updateStartTime() {
    if (this.start_hour) {
      this.start_hour = this.start_hour.replace(/[^0-9]/g, '');
    }
    if (this.start_min) {
      this.start_min = this.start_min.replace(/[^0-9]/g, '');
    }
    const timeVal = (this.start_hour || this.start_min) ? `${this.start_hour || '00'}:${this.start_min || '00'}` : '';
    this.editForm.get('start_time')?.setValue(timeVal, { emitEvent: false });
    this.editForm.get('start_time')?.markAsDirty();
  }

  updateEndTime() {
    if (this.end_hour) {
      this.end_hour = this.end_hour.replace(/[^0-9]/g, '');
    }
    if (this.end_min) {
      this.end_min = this.end_min.replace(/[^0-9]/g, '');
    }
    const timeVal = (this.end_hour || this.end_min) ? `${this.end_hour || '00'}:${this.end_min || '00'}` : '';
    this.editForm.get('end_time')?.setValue(timeVal, { emitEvent: false });
    this.editForm.get('end_time')?.markAsDirty();
  }

  isDirty(): boolean {
    return this.isFormDirty;
  }

  navBack(): void {
    this.router.navigate(['/system/shift']);
  }

  setShiftInfo(id: any): void {
    this.shiftService.getShiftById(id).subscribe({
      next: (response) => {
        if (response.status == 'SUCCESS') {
          this.shiftInfo = response.data.shift;
          this.editForm.patchValue({
            ...this.shiftInfo,
            start_time: this.shiftInfo.start_time ? this.shiftInfo.start_time.slice(0,5) : '09:00',
            end_time: this.shiftInfo.end_time ? this.shiftInfo.end_time.slice(0,5) : '17:00'
          });
          this.title = this.getTitle();
          this.isFormDirty = false;
        } else {
          this.snackBarService.openSnackBar({ message: response.message, title: 'Failed!!!', type: 'error' });
        }
      }
    });
  }

  getTitle(): string {
    return (!this.shiftInfo.id || this.shiftInfo.id == 0) ? "New Shift" : "Editing : " + this.shiftInfo.name;
  }

  save(): void {
    if (this.editForm.valid) {
      const data = { ...this.shiftInfo, ...this.editForm.value };

      // Validate valid time range (00 to 23 for hour, 00 to 59 for min)
      if (data.start_time && !this.isValidTime(data.start_time)) {
        this.snackBarService.openSnackBar({ message: 'Please enter a valid Start time (Hour 00-23, Minute 00-59).', title: 'Validation Error', type: 'error' });
        return;
      }
      if (data.end_time && !this.isValidTime(data.end_time)) {
        this.snackBarService.openSnackBar({ message: 'Please enter a valid End time (Hour 00-23, Minute 00-59).', title: 'Validation Error', type: 'error' });
        return;
      }

      const obs = (data.id && data.id > 0) ? this.shiftService.updateShift(data) : this.shiftService.addShift(data);
      
      obs.subscribe({
        next: (response) => {
          if (response.status == 'SUCCESS') {
            this.isFormDirty = false;
            this.snackBarService.openSnackBar({
              type: 'success',
              title: 'Saved',
              message: `Record for ${data.name} has been updated successfully.`
            });
            this.router.navigate(['/system/shift']);
          } else {
            this.snackBarService.openSnackBar({ message: response.message, title: 'Failed!!!', type: 'error' });
          }
        }
      });
    } else {
      this.editForm.markAllAsTouched();
    }
  }

  delete(): void {
    if (this.shiftInfo.id) {
      this.shiftService.deleteShift(this.shiftInfo.id).subscribe({
        next: (response) => {
          if (response.status == 'SUCCESS') {
            this.isFormDirty = false;
            this.snackBarService.openSnackBar({
              type: 'success',
              title: 'Deleted',
              message: `Record for ${this.shiftInfo.name} has been deleted successfully.`
            });
            this.router.navigate(['/system/shift']);
          } else {
            this.snackBarService.openSnackBar({ message: response.message, title: 'Failed!!!', type: 'error' });
          }
        }
      });
    }
  }

  private isValidTime(timeStr: string): boolean {
    if (!timeStr) return true;
    const parts = timeStr.split(':');
    if (parts.length !== 2) return false;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || h < 0 || h > 23) return false;
    if (isNaN(m) || m < 0 || m > 59) return false;
    return true;
  }
}
